// Mobile Draft Lottery screen — over ZenGM's desktop DraftLottery view. Phone-native flow built
// around a single probability GRID: each lottery team's exact odds for every pick slot
// (Record, Chances, 1st…Nth), sticky team column + horizontal scroll (same pattern as GmDraft's Big
// Board). The user row is tinted. Before the draw, each team's likeliest cell is highlighted; after
// "Start Lottery" fires the real weighted draw, the highlight moves to the pick each team ACTUALLY
// landed (teal fill + ✓), so the grid doubles as the result board.
//
// Real data: `draftLotteryResult` + `probs` from the draftLottery worker view drive the grid.
// The exact analytic probabilities from getDraftLotteryProbs feed the percentages (no Monte-Carlo).
// "Start Lottery" fires main.draftLottery, replacing the projected result with the actual draw (rows
// gain real `pick` values). "Continue to Draft" fires the shell's `untilDraft` play-menu action,
// entering the DRAFT phase (which redirects to the board).

import { useEffect, useRef, useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { useLocal } from "../util/local.ts";
import { toWorker } from "../util/toWorker.ts";
import { NO_LOTTERY_DRAFT_TYPES, PHASE } from "../../common/constants.ts";
import { getDraftTypeDescription } from "./DraftLottery.tsx";
import type {
	View,
	DraftLotteryResult,
	DraftLotteryResultArray,
} from "../../common/types.ts";

const TEAL = "#18E0A8";
// Lottery reveal: the green landed-pick boxes light up one at a time, from the last lottery
// pick up to #1 (like the real NBA reveal), so the drama builds to the top selection.
const LOTTERY_REVEAL_MS = 620;
// Compact grid column widths, kept in sync between the header row, the cells, and min-width.
const ODD_W = 40;
const TEAM_W = 128;

type Row = DraftLotteryResultArray<false>[number];

const GmLottery = (props: View<"draftLottery">) => {
	const noLottery = NO_LOTTERY_DRAFT_TYPES.has(props.draftType as any);
	useTitleBar({ title: noLottery ? "Draft Order" : "Draft Lottery" });

	const { userTid, phase } = useLocal(["userTid", "phase"]);

	// The live draw's rows (with real `pick` values), set once "Start Lottery" fires. Until then we
	// render the projected/completed result straight from the worker view.
	const [drawnRows, setDrawnRows] = useState<Row[] | null>(null);
	const mounted = useRef(true);

	// Reveal animation state. revealedFloor is the highest pick NOT yet revealed: a row is shown
	// as landed once its pick number is greater than the floor. floor === 0 means "all revealed"
	// (the resting state, and how a completed lottery we're revisiting renders — no animation).
	const [revealedFloor, setRevealedFloor] = useState(0);
	const [animating, setAnimating] = useState(false);
	const revealTimer = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		return () => {
			mounted.current = false;
			if (revealTimer.current) {
				clearInterval(revealTimer.current);
			}
		};
	}, []);

	// Skip the reveal (tap the grid): jump straight to the fully-revealed order.
	const finishReveal = () => {
		if (revealTimer.current) {
			clearInterval(revealTimer.current);
			revealTimer.current = null;
		}
		setRevealedFloor(0);
		setAnimating(false);
	};

	// The projected/completed result from the worker feeds the grid. If we've fired a live draw this
	// session, prefer those rows.
	const projected = props.draftLotteryResult?.result;
	const rows: Row[] | undefined = drawnRows ?? projected;
	const N = rows?.length ?? 0;
	const probs = props.probs;

	// A lottery is "drawn" once its rows carry real `pick` values (either a live draw this session or
	// a completed lottery we're revisiting). Before that we're in the projected state.
	const drawn = !!rows?.some((r) => r.pick !== undefined);

	const teams = props.teams as Record<
		number,
		{
			seasonAttrs?: { abbrev?: string; colors?: string[] };
			region?: string;
			name?: string;
		}
	>;
	const abbrevOf = (tid: number) => teams[tid]?.seasonAttrs?.abbrev ?? "—";
	const colorOf = (tid: number) =>
		teams[tid]?.seasonAttrs?.colors?.[0] ?? "#4A9EFF";
	const nameOf = (tid: number) => {
		const t = teams[tid];
		return t?.region || t?.seasonAttrs?.abbrev || `Team ${tid}`;
	};
	// Traded pick: the current owner (tid) differs from the record team (originalTid).
	const fromAbbrev = (row: Row) =>
		row.tid !== row.originalTid ? abbrevOf(row.originalTid) : undefined;

	// Fire the real weighted draw. Returns result rows with `pick` filled in; we swap them into the
	// grid so the actual-landed-pick highlight lights up.
	const startLottery = async () => {
		const draw = (await toWorker("main", "draftLottery", undefined)) as
			| DraftLotteryResult<false>
			| undefined;
		if (!draw || !mounted.current) return;
		const result = draw.result;
		setDrawnRows(result);

		// Kick off the back-to-front reveal: start with nothing revealed (floor === N) and walk
		// the floor down to 0, exposing one more pick each tick from the last pick up to #1.
		setAnimating(true);
		setRevealedFloor(result.length);
		let floor = result.length;
		if (revealTimer.current) {
			clearInterval(revealTimer.current);
		}
		revealTimer.current = setInterval(() => {
			if (!mounted.current) {
				if (revealTimer.current) clearInterval(revealTimer.current);
				return;
			}
			floor -= 1;
			setRevealedFloor(floor);
			if (floor <= 0) {
				if (revealTimer.current) {
					clearInterval(revealTimer.current);
					revealTimer.current = null;
				}
				setAnimating(false);
			}
		}, LOTTERY_REVEAL_MS);
	};

	// "Continue to Draft" enters the DRAFT phase via the shell's play-menu action, which redirects to
	// the mobile draft board. Mirrors GmShell's `untilDraft`.
	const startDraft = () => {
		void toWorker("playMenu", "untilDraft" as any, undefined);
	};

	// A row shows its landed pick once the draw exists and — while the reveal is animating — once
	// the reveal floor has descended past that pick number. Not animating ⇒ everything shown.
	const rowLanded = (row: Row) =>
		drawn &&
		row.pick !== undefined &&
		(!animating || row.pick > revealedFloor);

	// The user's landed pick number, revealed only when their row is revealed (keeps the banner
	// from spoiling the pick before the animation reaches it).
	const userRow = rows?.find((r) => r.tid === userTid);
	const userPickNo =
		userRow && rowLanded(userRow) ? userRow.pick : undefined;

	// --- banner ---
	let bannerBg: string;
	let bannerBorder: string;
	let eyebrow: string;
	let eyebrowColor: string;
	let bannerTitle: string;
	let bannerSub: string;
	if (drawn && animating) {
		bannerBg = "linear-gradient(135deg,#1c2b24,#161a17)";
		bannerBorder = "#2f6b52";
		eyebrow = noLottery ? "REVEALING ORDER" : "REVEALING LOTTERY";
		eyebrowColor = "#7fe8c0";
		bannerTitle =
			userPickNo !== undefined
				? `${nameOf(userTid)} lands pick #${userPickNo}`
				: "Revealing the draft order…";
		bannerSub = "Revealed from the last pick up — tap the grid to skip.";
	} else if (drawn) {
		bannerBg = "linear-gradient(135deg,#123a2e,#152218)";
		bannerBorder = "#1f5c45";
		eyebrow = noLottery ? "DRAFT ORDER SET" : "LOTTERY COMPLETE";
		eyebrowColor = "#7fe8c0";
		bannerTitle =
			userPickNo !== undefined
				? `${nameOf(userTid)} lands pick #${userPickNo}`
				: "The order is set";
		bannerSub = "Each team's landed pick is marked below — scout the class.";
	} else {
		bannerBg = "linear-gradient(135deg,#242424,#181818)";
		bannerBorder = "#2a2a2a";
		eyebrow = noLottery ? "DRAFT ORDER" : "DRAFT LOTTERY";
		eyebrowColor = "#9fb0bc";
		bannerTitle = noLottery
			? "Reverse-standings order"
			: props.draftType === "nba2027"
				? "3-2-1 weighted lottery"
				: "Weighted lottery";
		bannerSub =
			props.type === "projected"
				? "Projected odds if the lottery were held right now."
				: "Weighted for all lottery picks · restrictions for repeat top seeds.";
	}

	// Grid columns: 1st…Nth (numToPick columns; fall back to all picks).
	const oddCols = props.numToPick > 0 ? props.numToPick : N;

	const gridHint = drawn
		? "Pick probability · swipe the table · green box = the pick each team landed"
		: "Pick probability · swipe the table · brightest cell is each team's likeliest slot";

	return (
		<div className="gml">
			<style>{styles}</style>

			<div className="gml-scroll">
				{/* state banner */}
				<div
					className="gml-banner"
					style={{ background: bannerBg, borderColor: bannerBorder }}
				>
					<div className="gml-banner-eyebrow" style={{ color: eyebrowColor }}>
						{eyebrow}
					</div>
					<div className="gml-banner-title">{bannerTitle}</div>
					<div className="gml-banner-sub">{bannerSub}</div>
				</div>

				{/* ============ PROBABILITY GRID ============ */}
				<div className="gml-hint">{gridHint}</div>
				{!rows || N === 0 || !probs ? (
					<div className="gml-empty">No odds available for {props.season}.</div>
				) : (
					<div
						className="gml-tablewrap"
						onClick={animating ? finishReveal : undefined}
						style={animating ? { cursor: "pointer" } : undefined}
					>
						<div
							className="gml-table"
							style={{ minWidth: TEAM_W + oddCols * ODD_W }}
						>
							<div className="gml-thead">
								<div className="gml-th-team">TEAM</div>
								<div className="gml-cells">
									{Array.from({ length: oddCols }, (_, p) => (
										<div className="gml-th" style={{ width: ODD_W }} key={p}>
											{helpers.ordinal(p + 1)}
										</div>
									))}
								</div>
							</div>
							{rows.map((row, i) => {
								const isUser = row.tid === userTid;
								const teamProbs = probs[i] ?? [];
								// Which cell to highlight: once this row's pick is revealed, the slot it
								// actually landed (0-based); before that (pre-draw or awaiting reveal),
								// its likeliest slot.
								const landedRow = rowLanded(row);
								let markIdx = -1;
								if (landedRow && row.pick !== undefined) {
									markIdx = row.pick - 1;
								} else {
									let maxP = -1;
									for (let p = 0; p < oddCols; p++) {
										const v = teamProbs[p];
										if (v !== undefined && v > maxP) {
											maxP = v;
											markIdx = p;
										}
									}
								}
								const from = fromAbbrev(row);
								return (
									<div
										className={`gml-trow ${isUser ? "gml-trow-user" : ""}`}
										key={i}
									>
										<div
											className="gml-td-team"
											style={{
												background: isUser ? "#14211d" : "#121212",
											}}
										>
											<div className="gml-seed">{i + 1}</div>
											<div
												className="gml-badge gml-badge-sm"
												style={{ background: colorOf(row.tid) }}
											>
												{abbrevOf(row.tid)}
											</div>
											<div className="gml-td-abbr">
												{abbrevOf(row.tid)}
												{from ? (
													<span className="gml-from"> ·{from}</span>
												) : null}
											</div>
										</div>
										<div className="gml-cells">
											{Array.from({ length: oddCols }, (_, p) => {
												const v = teamProbs[p];
												const isMark = p === markIdx;
												// A revealed landed-pick cell becomes a solid teal pill showing the
												// pick number (1, 2, 3…) instead of a percentage — even if the
												// analytic odds for that slot were tiny (or zero for a jump).
												const landed = landedRow && isMark;
												// Percentages render as whole integers ("5", not "5.0"); anything
												// that rounds below 1% collapses to a dot to keep the grid compact.
												const pctNum = v === undefined ? 0 : Math.round(v * 100);
												const pct = pctNum < 1 ? "·" : `${pctNum}`;
												return (
													<span
														className="gml-cell"
														style={{
															width: ODD_W,
															color: landed
																? "#053a2c"
																: isMark
																	? TEAL
																	: pctNum >= 1
																		? "#cfcfcf"
																		: "#4a545b",
															fontWeight: isMark ? 900 : 700,
														}}
														key={p}
													>
														{landed ? (
															<span className="gml-pill">{row.pick}</span>
														) : (
															pct
														)}
													</span>
												);
											})}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}
				<div className="gml-oddstype">
					{getDraftTypeDescription(props.draftType ?? "noLottery")}
				</div>
			</div>

			{/* footer CTA — shown whenever we're still at the DRAFT_LOTTERY phase (live flow): the
			    readyToRun pre-draw case, plus a "completed" draw that hasn't advanced to the draft
			    yet (revisiting the screen after the reveal). A truly historical lottery
			    (phase past DRAFT_LOTTERY) has no draft to enter, so no footer. */}
			{props.type === "readyToRun" ||
			(props.type === "completed" && phase === PHASE.DRAFT_LOTTERY) ? (
				<div className="gml-footer">
					<div
						className="gml-cta"
						onClick={drawn ? () => startDraft() : () => startLottery()}
					>
						{drawn
							? "Continue to Draft ▸"
							: noLottery
								? "Reveal order ▸"
								: "Start Lottery ▸"}
					</div>
				</div>
			) : null}
		</div>
	);
};

const styles = `
.gml { position:relative; color:#fff; font-family:Montserrat,system-ui,sans-serif; }
.gml ::-webkit-scrollbar { width:0; height:0; }
.gml-scroll { -ms-overflow-style:none; scrollbar-width:none; padding-bottom:150px; }

.gml-banner { border-radius:16px; padding:15px 17px; margin:14px 2px 0; border:1px solid; }
.gml-banner-eyebrow { font-size:10.5px; font-weight:900; letter-spacing:1.4px; }
.gml-banner-title { font-size:22px; font-weight:900; letter-spacing:-.5px; margin-top:4px; }
.gml-banner-sub { font-size:11px; font-weight:700; color:#9fb0bc; margin-top:3px; }

.gml-hint { padding:14px 2px 0; font-size:10.5px; font-weight:700; color:#6f7b83; }
.gml-empty { text-align:center; padding:34px 16px; font-size:12.5px; color:#7a7a7a; font-weight:700; }

.gml-badge { width:30px; height:30px; border-radius:9px; flex:none; display:flex;
  align-items:center; justify-content:center; font-size:9.5px; font-weight:900; color:#0b0f13; }
.gml-badge-sm { width:24px; height:24px; border-radius:7px; font-size:8px; }
.gml-from { font-size:10.5px; font-weight:700; color:#7f8b93; }

.gml-tablewrap { overflow-x:auto; margin-top:10px; padding:0 0 4px;
  -ms-overflow-style:none; scrollbar-width:none; }
.gml-tablewrap::-webkit-scrollbar { width:0; height:0; }
.gml-thead { display:flex; align-items:flex-end; padding-bottom:8px; border-bottom:1px solid #2a2a2a; }
.gml-th-team { position:sticky; left:0; z-index:2; width:128px; flex:none; background:#121212;
  font-size:8.5px; font-weight:800; color:#6f7b83; letter-spacing:.5px; padding-left:2px; }
.gml-cells { display:flex; flex:none; }
.gml-th { flex:none; text-align:center; font-size:8.5px; font-weight:800; letter-spacing:.3px;
  color:#7f8b93; }
.gml-trow { display:flex; align-items:center; padding:9px 0; border-top:1px solid #202020; }
.gml-trow-user { background:rgba(24,224,168,.05); }
.gml-td-team { position:sticky; left:0; z-index:2; width:128px; flex:none; display:flex;
  align-items:center; gap:8px; padding-right:10px; }
.gml-seed { font-size:11px; font-weight:900; color:#5f6b73; width:16px; flex:none; text-align:center; }
.gml-td-abbr { min-width:0; font-size:12px; font-weight:800; white-space:nowrap; overflow:hidden;
  text-overflow:ellipsis; }
.gml-cell { flex:none; text-align:center; font-size:11.5px; display:flex; align-items:center;
  justify-content:center; }
/* Landed-pick pill: a fixed, centered teal box so pick numbers line up cleanly over their
   column without bleeding into neighbors. */
.gml-pill { display:inline-flex; align-items:center; justify-content:center; min-width:24px;
  height:22px; padding:0 5px; border-radius:7px; background:#18E0A8; color:#053a2c; font-weight:900;
  box-shadow:0 0 0 1px rgba(24,224,168,.45); }
.gml-oddstype { padding:16px 2px 0; font-size:10.5px; font-weight:600; color:#6f7b83; line-height:1.5; }

.gml-footer { position:fixed; left:50%; transform:translateX(-50%); bottom:128px; z-index:900;
  width:calc(100% - 28px); max-width:732px; }
.gml-cta { text-align:center; padding:15px; border-radius:26px; font-size:14px; font-weight:900;
  background:#18E0A8; color:#053a2c; cursor:pointer; box-shadow:0 10px 26px rgba(24,224,168,.28); }
`;

export default GmLottery;
